import React from 'react'

import * as constants from '../../../../../static/constants'
import styled from 'styled-components'
import { Button, Counter } from 'components/atoms'
import { IAges, ISelectedGuests } from 'utils/reservation'
import { IPost, IMemberPost } from 'static/interfaces'
import Typography from 'components/atoms/_layouts/Typography'
import FlashMessage from 'components/organisms/FlashMessage'

interface IWindow {
  flashMessages: FlashMessage
}
declare var window: IWindow

interface IMultiDatePickerProps {
  post: IPost | IMemberPost
  isPrivate: boolean
  guests: ISelectedGuests
  ages: IAges
  disabled?: boolean
  align?: 'left' | 'right'
  departure?: {
    available_count: number
    limit_people: number
  }
  handleUpdateLocalDataByKey?: (key: string, value: any) => void
  setGuests:
    | React.Dispatch<React.SetStateAction<ISelectedGuests>>
    | ((prevState: ISelectedGuests) => void)
  setIsOpenGuestCount: React.Dispatch<React.SetStateAction<boolean>>
}

export const GuestsCounter: React.VFC<IMultiDatePickerProps> = ({
  post,
  guests,
  isPrivate,
  ages,
  disabled,
  align = 'right',
  departure,
  setGuests,
  setIsOpenGuestCount,
  handleUpdateLocalDataByKey,
}) => {
  const [selectedGuests, setSelectedGuests] = React.useState<ISelectedGuests>({
    adults: guests.adults,
    children: guests.children ?? 0,
    infants: guests.infants ?? 0,
  })

  const maxCount = React.useMemo(() => {
    const bookingMaxPeople = Number(post.booking_max_people)
    const limit = departure?.limit_people
    if (isPrivate) {
      return bookingMaxPeople
    }

    const max = bookingMaxPeople
    // 開始日未選択の時 -> P1
    if (limit === undefined) {
      return max
    }
    // 開始日が存在する時 -> P2
    return limit > max ? max : limit
  }, [isPrivate, departure?.limit_people, post.booking_max_people, post.max_people])

  const allGuestCount = React.useMemo(() => {
    return selectedGuests.adults + selectedGuests.children + selectedGuests.infants
  }, [selectedGuests.adults, selectedGuests.children, selectedGuests.infants])

  const isLimit = React.useMemo(() => {
    if (post.family_owner) {
      return false
    }
    return allGuestCount >= maxCount
  }, [allGuestCount, maxCount])

  const overLimit = React.useMemo(() => {
    if (post.family_owner) {
      return false
    }
    return allGuestCount > maxCount
  }, [allGuestCount, maxCount])

  const applyGuests = React.useCallback(() => {
    const updatedGuests = { ...selectedGuests }

    const hasAnyGuests =
      selectedGuests.adults > 0 || selectedGuests.children > 0 || selectedGuests.infants > 0
    if (!hasAnyGuests) {
      updatedGuests.adults = 1
      window.flashMessages.addMessage({
        text: 'Please select at least one guest.',
        type: 'error',
      })
    }
    const isInfantsWithoutAdults = selectedGuests.infants > 0 && selectedGuests.adults === 0
    if (isInfantsWithoutAdults) {
      updatedGuests.adults = 1
      window.flashMessages.addMessage({
        text: 'Please select at least one adult.',
        type: 'error',
      })
    }
    handleUpdateLocalDataByKey('guests', updatedGuests)
    setGuests(updatedGuests)
    setIsOpenGuestCount(false)
  }, [selectedGuests])

  return (
    <GuestsCountWrapper align={align}>
      {!isPrivate && (
        <div className="GroupTourNote">
          {!post.family_owner && !isPrivate !== null && (
            <Typography type="body" size="md">
              Maximum Travelers per Group :{maxCount}
            </Typography>
          )}
          <p className="SplitNote">
            * You can book above the maximum capacity, but your group may be split
          </p>
        </div>
      )}
      <div className="GuestCountContainer">
        {isPrivate ? (
          <div className="GuestCount">
            <div className="GuestCountLabel">
              <p className="label">Guests</p>
            </div>
            <Counter
              defaultValue={selectedGuests.adults}
              minValue={1}
              handleChange={count => {
                setSelectedGuests(old => ({ ...old, adults: count }))
              }}
              isLimit={isLimit}
              changeableCountLabel={true}
            />
          </div>
        ) : (
          <>
            <div className="GuestCount">
              <div className="GuestCountLabel">
                <p className="label">Adults</p>
              </div>
              <Counter
                defaultValue={guests.adults}
                minValue={0}
                handleChange={count => {
                  setSelectedGuests(old => ({ ...old, adults: count }))
                }}
                isLimit={isLimit}
                changeableCountLabel={true}
              />
            </div>
            <div className="GuestCount">
              <div className="GuestCountLabel">
                <p className="label">Children</p>
              </div>
              <Counter
                defaultValue={guests.children}
                handleChange={count => {
                  setSelectedGuests(old => ({ ...old, children: count }))
                }}
                isLimit={isLimit}
                changeableCountLabel={true}
              />
            </div>
            <div className="GuestCount">
              <div className="GuestCountLabel">
                <p className="label">Infants</p>
              </div>
              <Counter
                defaultValue={guests.infants}
                handleChange={count => {
                  setSelectedGuests(old => ({ ...old, infants: count }))
                }}
                isLimit={isLimit}
                changeableCountLabel={true}
              />
            </div>
          </>
        )}
      </div>
      <div className="Buttons">
        <Button
          handleClick={() => {
            handleUpdateLocalDataByKey('guests', constants.DEFAULT_SEARCH_FILTER_GUESTS)
            setSelectedGuests(constants.DEFAULT_SEARCH_FILTER_GUESTS)
            setGuests(constants.DEFAULT_SEARCH_FILTER_GUESTS)
            setIsOpenGuestCount(false)
          }}
          large={true}
          textColor={constants.COLORS.subText}
          borderColor={constants.COLORS.divider}
        >
          Reset
        </Button>
        <Button
          disabled={disabled || overLimit}
          handleClick={applyGuests}
          primary={true}
          large={true}
        >
          Save
        </Button>
      </div>
    </GuestsCountWrapper>
  )
}

const GuestsCountWrapper = styled.div`
  position: absolute;
  width: 100%;
  max-width: 328px;
  z-index: 10;
  background-color: ${constants.COLORS.white};
  padding: 16px;
  border-radius: 16px;
  box-shadow: ${constants.COLORS.cardShadow};
  padding: 24px;
  left: ${({ align }) => (align === 'left' ? 0 : 'unset')};
  right: ${({ align }) => (align === 'right' ? 0 : 'unset')};
  @media (max-width: ${constants.BREAKPOINT_TABLET_SMALL}px) {
    margin-left: 16px;
  }
  @media (max-width: ${constants.BREAKPOINT_TABLET_MOBILE}px) {
    margin-left: 0;
    > div {
      margin: 0 auto;
    }
  }
  .GroupTourNote {
    margin-bottom: 1.6rem;
    p:nth-child(2) {
      margin-top: 0.4rem;
    }
    .SplitNote {
      font-size: 1.1rem;
    }
  }
  .GuestCount {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-top: 1.6rem;
    margin-bottom: 1.6rem;
    border-top: 1px solid ${constants.COLORS.divider2};
    &:first-child {
      border: 0;
      padding-top: 0;
    }
    .number {
      font-size: 1.6rem;
    }
    .label {
      line-height: 2.4rem;
      font-size: 1.6rem;
    }
    .age {
      line-height: 1.6rem;
      font-size: 1.1rem;
      color: ${constants.COLORS.subText};
    }
  }
  .Buttons {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    padding-top: 0.4rem;
    @media (max-width: ${constants.BREAKPOINT_TABLET_MOBILE}px) {
      justify-content: center;
      padding: 0.8rem 0 !important;
    }
    button {
      width: 11rem;
      & + button {
        margin-left: 1.6rem;
      }
    }
  }
`
